<template>
  <nk-page commercialAccess="Salary" class="position-code" policyClaims="Salary_Position_Management">
    <template #badges>
      <nk-badge system="salary">
        <nk-badge-item type="system" modelValue="salary"></nk-badge-item>
        <nk-badge-item type="system" modelValue="salary" :title="$t('forms.Position')"></nk-badge-item>
      </nk-badge>
    </template>
    <div class="nk-card h-100 d-flex flex-column" startup>
      <div class="h-grow-lock">
        <nk-table v-model="selectedRow" @data-changed="dataChanged" :data="positions" :options="tableOptions" ref="positionList" tabindex="101" @row_dbClicked="rowDbClicked">
        </nk-table>
      </div>
    </div>
    <download-excel ref="exportExcel" class="btn btn-default d-none" :data="positions" :fields="exportExcelFields()" :worksheet="$t('forms.Positions')" type="xlsx" :name="`${$t('forms.Position')}.xlsx`"></download-excel>
    <input type="file" ref="excelFileInput" class="d-none" @change="changeInputExcelFile" accept=".xlsx,.xls" />
  </nk-page>
</template>
<script lang="ts">
  import Input from '~/components/general/nikan-input.vue'
  import $sc from '~/plugins/shortcut.collection.js'
  import dialog from '~/plugins/nikan-dialog-plugin'
  import validators from '~/statics/validators'
  import { defineComponent } from 'vue'
  import { post } from '~/plugins/nikan-api'
  import Endpoints from '@nikan/endpoints'
  import { IResponseList } from '@/interfaces/base'
  import IsEmpty from 'lodash/isEmpty'
  import { TYPE } from 'vue-toastification'
  import { Position } from '@/classes/salary/position'
  import { importExcel } from '@/plugins/helper'
  import emptyId from '@/statics/empty-id.js'
  import ProductImportErrors from '@/views/sale/products/product-import-errors.vue'

  interface IExcelRow {
    id?: string
    socialSecurityCode: string
    title: string
    description?: string
    rowState?: string
  }

  export default defineComponent({
    name: 'position-selector',
    modal: {
      size: 'custom',
      width: 900,
      height: 600,
      stateUID: 'position-selector',
    },
    props: {
      options: { type: Object },
    },
    data() {
      return {
        positions: [] as Position[],
        selectedRow: null as Position | null,
        tableOptions: {
          activeRowDisabled: true,
          editable: this.$nikan.checkPermission({ or: ['Salary_Position_Add', 'Salary_Position_Delete'] }),
          searchable: false,
          allowNewRow: true,
          alwaysEmit: true,
          buttons: {
            addBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_Position_Add',
              },
              displayOrder: 1,
            },
            deleteBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_Position_Delete',
              },
              displayOrder: 2
            },
            importFromExcelBtn: {
              handler: this.importFromExcel,
              class: "no-outline",
              type: 'nk-btn',
              options: {
                isPrimary: false,
                hideShortcut: true,
                title: this.$t('sale.receipt-hawala.buttons.import-product-from-excel'),
                visible: true,
                tooltipEnabled: true,
                hideTitle: false,
              },
              displayOrder: 3,
            },
            downloadExcelSampleBtn: {
              handler: this.downloadSample,
              class: "no-outline",
              type: 'nk-btn',
              options: {
                isPrimary: false,
                // shortcut: $sc.find('search'),
                hideShortcut: true,
                title: this.$t('sale.receipt-hawala.buttons.download-excel-sample'),
                visible: true,
                // icon: 'plus',
                tooltipEnabled: true,
                hideTitle: false,
              },
              displayOrder: 4,
            },
            exportToExcelBtn: {
              handler: this.exportToExcel,
              class: "no-outline",
              type: 'nk-btn',
              options: {
                isPrimary: false,
                hideShortcut: true,
                title: this.$t('sale.receipt-hawala.buttons.export-to-excel'),
                visible: true,
                tooltipEnabled: true,
                hideTitle: false,
              },
              displayOrder: 5,
            }
          },
          displayOrderEnabled: false,
          prepareNewRow: () => new Position(),
          columns: [
            {
              name: 'rowNumber',
              title: this.$t('row-number'),
              searchable: true,
              sortable: true,
              editable: false,
              width: 20,
              entering: false,
              pinned: true,
              pinnable: false,
              resizable: false,
            }, {
              name: 'socialSecurityCode',
              title: this.$t('salary.contract.position.f.code'),
              edit: {
                component: Input,
                options: {
                  mode: 'strnumber',
                },
              },
              searchable: true,
              sortable: true,
              editable: true,
              width: 40,
              entering: true,
              pinnable: true,
            }, {
              name: 'title',
              title: this.$t('salary.contract.position.f.title'),
              edit: {
                component: Input,
                options: {
                  mode: 'string',
                },
              },
              searchable: true,
              sortable: false,
              editable: true,
              width: 150,
              validators: validators('required'),
            }
          ],
        },
        pageButtons: {
          saveButton: {
            title: this.$t('save'),
            shortcut: $sc.find('save'),
            icon: 'save',
            visible: true,
            disabled: true,
            isPrimary: true,
            handle: () => {
              this.save().then(() => {
                this.$nikan.formUtility.switchMode(this.pageButtons, 'select',)
                this.$refs.positionList.table_Focused()
              })
            },
            policyClaims: { or: ['Salary_Position_Add', 'Salary_Position_Delete'] },
          },
          selectButton: {
            title: this.$t('select'),
            shortcut: $sc.find('save'),
            icon: 'check',
            isPrimary: true,
            visible: true,
            disabled: false,
            handle: () => {

              if (IsEmpty(this.selectedRow))
                return this.$toast(this.$t('document.m.select-row'))

              this.resolve(this.selectedRow)
            }
          }
        }
      }
    },
    mounted() {
      this.prepareWindow(this.$t('salary.contract.position.title'), [this.pageButtons.selectButton, this.pageButtons.saveButton])
      this.loadPositions()
    },
    methods: {
      save(): Promise<Position[]> {
        return new Promise((resolve, reject) => {
          this.$refs.positionList
            .commitChanges()
            .then((positions: (Position & { rowState: string })[]) => {

              const wait = this.$wait(this.$el)
              post(Endpoints.getUrl('position.modify'), { items: positions })
                .then(() => {
                  this.$toast(this.$t('salary.contract.position.m.save-success'), { type: TYPE.SUCCESS })
                  this.loadPositions()
                  resolve(positions)
                })
                .finally(() => {
                  wait.end()
                })


            })
            .catch((errorRows: { message: string } | { rowNumber: number }[]) => {
              if (Array.isArray(errorRows)) {
                var rowNumbers = errorRows.map((x, i) => x.rowNumber).join(',')
                dialog({
                  title: this.$t('salary.contract.position.m.save-codes-title'),
                  body: this.$t('salary.contract.position.m.error-rows', { rowNumbers }),
                  type: 'error',
                  isPrimary: true,
                })
              } else {
                dialog({
                  title: this.$t('salary.contract.position.m.save-codes-title'),
                  body: errorRows.message,
                  type: 'error',
                  isPrimary: true,
                })
              }

              reject(new Error('invalid-rows'))
              this.defer(() => this.$refs.positionList.$el.focus(), 100)
            })
        })
      },
      dataChanged() {
        this.$nikan.formUtility.switchMode(this.pageButtons, 'edit')
      },
      rowDbClicked(val: Position): void {
        if (this.grandParentType() === 'modal')
          this.resolve(val)
      },
      loadPositions(): void {
        const wait = this.$wait(this.$el)
        post(Endpoints.getUrl('position.listDynamic'))
          .then((res: IResponseList<Position>) => {
            this.positions = res.data.items
          })
          .finally(() => {
            wait.end()
          })
      },
      downloadSample() {
        const type = 'PositionSelector'
        post(`/api/v1.0/system/Download/Download?fileName=nikan-${type.toLowerCase()}-excel-sample.xlsx`, null)
          .then((res: any) => {
            const url = window.URL.createObjectURL(
              new Blob([res.data], { type: res.headers['content-type'] })
            )
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            // the filename you want
            a.download = `nikan-${type.toLowerCase()}-excel-sample.xlsx`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
          })
          .catch(() => { })
      },
      importFromExcel() {
        this.$refs.excelFileInput.value = null
        this.$refs.excelFileInput.click()
      },
      changeInputExcelFile(event: Event) {
        importExcel(event, this.exportExcelFields()).then(async res => {
          if (res) {
            const errors: IExcelRow[] = []

            const addError = (row: IExcelRow, description: string) => {
              errors.push({ socialSecurityCode: row.socialSecurityCode, title: row.title, description })
            }

            const rows = this.$refs.positionList.getTableData().data
            const rowCodes: string[] = this.$refs.positionList.getTableData().data.map((row: Position) => row.socialSecurityCode)

            res.forEach((x: IExcelRow) => {

              if (!x.socialSecurityCode || !x.title)
                addError(x, this.$t('salary.contract.position.m.code-title-required'))

              if (x.socialSecurityCode && rowCodes.includes(String(x.socialSecurityCode)))
                addError(x, this.$t('salary.contract.position.m.code-duplicate'))

              x.id = emptyId
              x.rowState = 'new'
              x.socialSecurityCode = String(x.socialSecurityCode)

              rows.push(x)
              rowCodes.push(x.socialSecurityCode)
            })
            if (errors.length)
              await this.showModal({
                component: ProductImportErrors,
                data: errors,
                options: {
                  columnDefs: [
                    {
                      field: 'rowNumber',
                      headerName: this.$t('row-number'),
                      width: 50,
                    },
                    {
                      field: 'socialSecurityCode',
                      headerName: this.$t('salary.contract.position.f.code'),
                      width: 100,
                      // contentType: 'number'
                    },
                    {
                      field: 'title',
                      headerName: this.$t('salary.contract.position.f.title'),
                      // width: 100,
                    },
                    {
                      field: 'description',
                      headerName: this.$t('sale.receipt-hawala.f.description'),
                      // width: 100,
                    },
                  ]
                }
              }).then(() => {
              }).catch(() => { })

            else {
              this.positions = rows
              this.$toast(this.$t('salary.bank-payment-announce.m.file-imported-successfully'), { type: TYPE.SUCCESS })
              this.$nikan.formUtility.switchMode(this.pageButtons, 'edit')
            }

          }
        }).catch((e) => {
          throw new Error(e)
        })
      },
      exportToExcel() {
        if (this.positions.length)
          this.defer(() => this.$refs.exportExcel.generate(), 0)
        else
          this.$toast(this.$t('m.no-data-for-export-to-excel'))
      },
      exportExcelFields() {
        return {
          [this.$t('salary.contract.position.f.code')]: 'socialSecurityCode',
          [this.$t('salary.contract.position.f.title')]: 'title',
        }
      }
    },
  })
</script>
<style></style>
